import React from "react";
import DialogBoxWithConfirmation from "../../util/DialogBoxWithConfirmation";
import { useTranslation } from "react-i18next";
import { URL_AUDIO } from "../../map/GPX/Resources";
import { roadmakerApi } from "../../../api/roadmakerApi";
import { normalizeFileName } from "../../util/Util";
import {
  discardPoiMediaUploads,
  pendingPoiMediaPreview,
  republishMarkerMedia,
} from "../../../api/poiMediaUploads";
import {
  findServerMediaByName,
  resolveMediaSource,
} from "./mediaSource";

const MarkerAudio = ({
  markerAudio,
  marker,
  setMarkerAudio,
  markerAudioName,
  setMarkerAudioName,
  setMarkers,
}) => {
  const { t } = useTranslation();

  // Source rétrocompatible centralisée dans ./mediaSource.
  const mediaNameAt = (index) =>
    markerAudioName?.[index] ?? marker.audioName?.[index];

  const serverMediaAt = (index) =>
    findServerMediaByName(marker, "audio", mediaNameAt(index));

  // Une pièce importée mais pas encore envoyée est lue depuis le fichier
  // local : sans cela, l'aperçu n'apparaissait qu'après la sauvegarde. Même
  // correspondance de nom que la suppression ; un média serveur du même nom
  // garde sa propre source.
  const pendingPreviewAt = (index) => {
    const name = mediaNameAt(index);
    if (!name || serverMediaAt(index)) return undefined;
    return pendingPoiMediaPreview(
      marker.id,
      "audio",
      (item) =>
        item.file.name === name || normalizeFileName(item.file.name) === name
    );
  };

  // Source locale sans attribut type : le type réel d'un .mov
  // (video/quicktime) est refusé d'office par Chrome et Firefox, qui lisent
  // pourtant le fichier ; le navigateur reconnaît le contenu lui-même.
  const audioSource = (audio, index) => {
    const localUrl = pendingPreviewAt(index);
    if (localUrl) return { url: localUrl };
    return {
      url: resolveMediaSource(marker, "audio", { value: audio, index }),
      type: "audio/mpeg",
    };
  };

  // La clé suit la source : un <audio> ne relit pas un <source> modifié.
  const renderAudio = ({ url, type }) => (
    <audio key={url} controls className="audioInput">
      <source src={url} type={type} />
    </audio>
  );

  /**
   * Permet de lancer la suppression d'un fichier audio du marqueur
   * @param {integer} index - Index du fichier audio à supprimer du marqueur
   */
  const handleDeleteAudio = (index) => async (e) => {
    console.log("jest: Fonction de suppression est appelée");
    e.stopPropagation();
    const confirmDelete = await DialogBoxWithConfirmation({
      title: t("deleteAudioText"),
      text: t("deleteAudioConfirmation"),
      icon: "warning",
      cancelButtonText: t("no"),
      confirmButtonText: t("yes"),
    });

    // Confirmer la suppression
    if (confirmDelete) {
      // Suppression serveur si le média est persisté (ADR 0005) ; un
      // média local non encore sauvé est seulement retiré de l'état.
      const serverMedia = serverMediaAt(index);
        if (serverMedia) {
          if (!marker.serverId || !marker.Project_id) return;
        try {
          await roadmakerApi.poiMedia.remove(
            marker.Project_id,
            marker.serverId,
            serverMedia
          );
        } catch (error) {
          console.error("Échec de la suppression du média serveur :", error);
          return;
        }
        marker.media = (marker.media || []).filter(
          (media) => media.fileName !== serverMedia.fileName
        );
        // La consultation doit refléter la suppression sans rechargement.
        republishMarkerMedia(marker, setMarkers);
      }
      const name = mediaNameAt(index);
        const discarded = discardPoiMediaUploads(
        marker.id,
        (item) =>
          item.mediaType === "audio" &&
          (item.file.name === name || normalizeFileName(item.file.name) === name)
      );
        if (!serverMedia && marker.serverId && discarded === 0) return;
      setMarkerAudio(markerAudio.filter((_, i) => i !== index));
      setMarkerAudioName(markerAudioName.filter((_, i) => i !== index));
    }
  };

  return (
    <div>
      {markerAudio &&
        markerAudio.map((audio, index) => (
          <div key={index}>
            <div>{markerAudioName && markerAudioName[index]}</div>
            {renderAudio(audioSource(markerAudio[index], index))}
            <br />
            {(markerAudio.length > 1 ||
              (markerAudio.length === 1 && index === 0)) && (
              <button
                type="button"
                className="buttonDeleteFile mt-3 mb-3 me-2"
                onClick={handleDeleteAudio(index)}
                aria-label="delete"
              >
                <i className="bi bi-trash"></i>
              </button>
            )}
          </div>
        ))}
      {!markerAudio &&
        marker.audio &&
        marker.audio.map((audio, index) => (
          <div key={index}>
            <div>{marker.audioName && marker.audioName[index]}</div>
            {renderAudio(audioSource(marker.audio?.[index], index))}
            <br />
          </div>
        ))}
    </div>
  );
};

export default MarkerAudio;
